// Route-driven review-sitting tracking for the assessment report review pages.
//
// The sixteen RD_*.js pages have no shared layout or writer to hook, and
// adding start/stop logic to each would be sixteen chances for the behaviour
// to drift. Instead this component mounts once beside the page inside the two
// patient route wrappers — which only render their children once the loaded
// data belongs to the patient in the URL — so a sitting starts when the
// clinician can actually see paragraphs, not while the page is still loading,
// and no RD page is touched at all.
//
// The module codes MIRROR each page's own MODULE_PREFIX_CODE, so sitting rows
// join against that page's report_paragraph_* and report_approved rows without
// translation. That includes the oddity: RD_PR_v2.js declares module 'ci' in
// its own code, so its route maps to 'ci' here — faithfully, if surprisingly.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { startReviewTracking, stopReviewTracking } from './reviewSession';

// Route segment (the part after /patient/:patientId/) -> the module code the
// page's own events carry.
export const REVIEW_ROUTE_MODULES = {
  'review-document-ci': 'ci',
  'review-document-ci-v2': 'ci',
  'review-document-pr-v2': 'ci',
  'review-document-pr': 'pr',
  'review-document-dd': 'dd',
  'review-document-pad': 'pad',
  'review-document-ssi': 'ssi',
  'review-document-qb-check': 'qbCheck',
  'review-document-qb-test': 'qbTest',
  'review-document-pre-assessment-report': 'preAR',
  'review-document-semble-mdt': 'sembleMDT',
  'review-document-ci-autism': 'history',
  'review-document-ssi-autism': 'ssi',
  'review-document-obs-autism': 'obs',
  'review-document-dd-autism': 'dd',
  'review-document-pad-autism': 'pad',
  // CYP ADHD review pages emit with their flagPrefix ('ci' / 'ssi' / 'dd' /
  // 'pad'), matching how the autism DD/PAD pages share the adult codes.
  'review-document-ci-cyp-adhd': 'ci',
  'review-document-ssi-cyp-adhd': 'ssi',
  'review-document-dd-cyp-adhd': 'dd',
  'review-document-pad-cyp-adhd': 'pad',
};

/** The sitting identity for a pathname, or null when it is not a review page. */
export function reviewMetaForPath(pathname) {
  const match = /^\/patient\/([^/]+)\/([^/]+)\/?$/.exec(pathname || '');
  if (!match) return null;
  const module = REVIEW_ROUTE_MODULES[match[2]];
  if (!module) return null;
  return { patientId: match[1], module };
}

export default function ReviewSessionTracker() {
  const { pathname } = useLocation();
  const meta = reviewMetaForPath(pathname);
  const patientId = meta ? meta.patientId : null;
  const module = meta ? meta.module : null;

  useEffect(() => {
    if (!patientId || !module) return undefined;
    startReviewTracking('report', { patientId, module });
    return () => stopReviewTracking();
  }, [patientId, module]);

  return null;
}
